import { AppUtil, EnvUtil } from 'kuaidong-lib'
import { UploadPresignInfo, UploadPresignApph5 } from 'kuaidong-model'
import { ABaseTzyStudentAPI } from './ABase'
import axios, { Canceler } from 'axios'

export interface FileUploadResult {
  fileUrl: string
  fileName: string
}

export interface UploadOptions {
  file: File
  scene?: string
  maxSizeMB?: number
  onCancel?: () => void
  onProgress?: (percent: number) => void
}

export class AUpload {
  private static cancelRequest: Canceler | null = null
  private static CancelToken = axios.CancelToken

  /**
   * 通用文件上传方法（获取签名 + 上传到 OSS）
   */
  static async uploadFile(options: UploadOptions): Promise<FileUploadResult> {
    const { file, scene = 'registration-form', maxSizeMB = 10, onCancel, onProgress } = options

    // 文件大小验证
    if (file.size > maxSizeMB * 1024 * 1024) {
      throw new Error(`文件大小不能超过 ${maxSizeMB}MB`)
    }

    try {
      // 获取上传签名
      const presign = await AUpload.presignAPI(scene, file.name)

      // 上传到 OSS
      await axios
        .request({
          url: presign.file_upload_url,
          data: file,
          method: presign.http_method!.toLowerCase() as any,
          headers: { 'Content-Type': presign['content-type'] },
          cancelToken: new AUpload.CancelToken((cancel) => {
            AUpload.cancelRequest = cancel
          }),
          onUploadProgress: (e: any) => {
            if (e.total > 0 && onProgress) {
              const percent = (e.loaded / e.total) * 100
              onProgress(percent)
            }
          },
        })
        .catch((err) => {
          throw new Error(err.message || '上传失败')
        })

      return {
        fileUrl: presign.fetch_url,
        fileName: file.name,
      }
    } catch (error: any) {
      if (axios.isCancel(error)) {
        onCancel?.()
      }
      throw error
    }
  }

  /**
   * 取消当前上传
   */
  static cancelUpload() {
    AUpload.cancelRequest?.('取消上传')
    AUpload.cancelRequest = null
  }

  // 上传图片签名, 给学生用，仅验证user_id, 仅允许上传图片和视频
  static async imageUploadPresign(scene: string, filename: string): Promise<UploadPresignInfo> {
    return ABaseTzyStudentAPI.httpGET('/student/file_upload_presign', { scene, filename })
  }

  static async presign(
    scene = 'edit-teacher-info',
    filename: string,
    isApp = false,
    isPay = false
  ): Promise<UploadPresignInfo> {
    const apiUrl = isApp
      ? isPay
        ? `${process.env.VUE_APP_CRAZYROPE_MANAGE_API}/student/file_upload_presign` // 原先是支付系统提供api，去掉直接用体智云学生的上传接口
        : `${process.env.VUE_APP_CRAZYROPE_MANAGE_API}/async/common/file_upload_presign`
      : '/async/common/file_upload_presign'
    return AppUtil.httpGET(apiUrl, { scene, filename })
  }

  static async presignAPI(category: string, filename: string): Promise<UploadPresignInfo> {
    if (EnvUtil.isAgency()) {
      return AUpload.presignAgency(category, filename)
    }
    if (EnvUtil.isOperation()) {
      return AUpload.presignOperation(category, filename)
    }
    if (EnvUtil.isCampus() || EnvUtil.isSaas() || EnvUtil.isISC()) {
      return AUpload.presign(category, filename)
    }
    if (EnvUtil.isApph5()) {
      return AUpload.presign(category, filename, true)
    }

    throw Error('No relative api.')
  }

  static async markShared(): Promise<void> {
    return AppUtil.httpGET('/async/fission/sign/share/moments')
  }

  static async markUploaded(): Promise<void> {
    return AppUtil.httpGET('/async/fission/sign/upload/screenshot')
  }

  static async presignApph5(filename: string, contentType: string): Promise<UploadPresignApph5> {
    return AppUtil.httpPOST('/user/head_image_upload_sign', { filename, 'content-type': contentType })
  }

  static async presignAgency(category: string, filename: string, ossKey = ''): Promise<UploadPresignInfo> {
    const paths = ['txp']

    category && paths.push(category)
    filename && paths.push(filename)

    return AppUtil.httpPOST('/tr/coach/user/upload/presign', {
      filename,
      oss_key: ossKey || paths.join('/'),
    }).then((result: any) => ({
      http_method: result.http_method,
      file_upload_url: result.presign_url,
      ['content-type']: result.content_type,
      fetch_url: result.visit_url,
    }))
  }

  static async presignOperation(category: string, filename: string): Promise<UploadPresignInfo> {
    return AppUtil.httpPOST('/operation/system/oss/upload_presign', { filename, category }).then((result: any) => ({
      http_method: result.method,
      file_upload_url: result.url,
      ['content-type']: result.content_type,
      fetch_url: result.visit_url,
    }))
  }
}
